import { Trans } from '@lingui/react/macro';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { completeSetupWelcome, type HealthCenterDataType } from '@/api/healthCenter';
import { useCurrentInstance } from '@/api/organization';
import AIIcon from '@/assets/svg/ai-stars.svg?react';
import ChartIcon from '@/assets/svg/bx-bar-chart-alt.svg?react';
import ArrowIcon from '@/assets/svg/bx-chevron-right.svg?react';
import { Button, LinkButton } from '@/components/buttons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import Icon from '@/components/icon';
import { Text, Title } from '@/components/typography';
import { FEATURE_FLAG } from '@/constants/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useMe } from '@/providers/User';
import { ROUTES } from '@/router';

type SetupWelcomeProps = {
  healthCenterData?: HealthCenterDataType;
  isLoading: boolean;
};

const SetupWelcome = ({ healthCenterData, isLoading }: SetupWelcomeProps) => {
  const user = useMe();
  const { instance, mutate: refreshInstance } = useCurrentInstance();
  const hasAIVisibility = !!useFeatureFlag(FEATURE_FLAG.aiVisibility);
  const [dismissedInstance, setDismissedInstance] = useState<string>();
  // Explicit rollout boundary excludes accounts that existed before this feature.
  // An unset/invalid value disables the welcome without affecting routing or tiles.
  const rolloutValue = import.meta.env.VITE_SETUP_WELCOME_FROM ?? '';
  const rollout = Date.parse(rolloutValue);
  const validRollout =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(rolloutValue) &&
    Number.isFinite(rollout) &&
    new Date(rollout).toISOString() ===
      (rolloutValue.includes('.') ? rolloutValue : rolloutValue.replace('Z', '.000Z'));
  const eligible =
    !!instance &&
    validRollout &&
    Date.parse(instance.createdAt) >= rollout &&
    hasAIVisibility &&
    !!(user?.isAdmin || user?.isC99Superuser) &&
    !instance.setupWelcomeCompletedAt;
  const { mutate: recordProgress, status } = useMutation({
    mutationFn: completeSetupWelcome,
    retry: 1,
    onSuccess: (data) => {
      if (data.instances.completeSetupWelcome) refreshInstance();
    },
  });

  useEffect(() => {
    if (
      eligible &&
      !isLoading &&
      healthCenterData &&
      healthCenterData.onboardingProgress > 0 &&
      status === 'idle'
    ) {
      recordProgress();
    }
  }, [eligible, isLoading, healthCenterData, status, recordProgress]);

  const open =
    eligible &&
    !isLoading &&
    healthCenterData?.onboardingProgress === 0 &&
    dismissedInstance !== instance?.id;
  const handleDismiss = () => setDismissedInstance(instance?.id);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleDismiss();
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto p-6 sm:p-8"
      >
        <DialogHeader>
          <Text variant="footnote" color="dark-grey">
            <Trans>Welcome to Channel99</Trans>
          </Text>
          <DialogTitle>
            <Trans>What would you like to set up first?</Trans>
          </DialogTitle>
          <DialogDescription
            render={<Text variant="body2" color="dark-grey" />}
            className="text-gray-900"
          >
            <Trans>
              Choose where to begin. You can set up both and return to either at any time.
            </Trans>
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <article className="flex flex-col items-start gap-3 rounded-2xl border border-green-300 bg-primary/15 p-6">
            <Icon icon={AIIcon} size="large" aria-hidden="true" />
            <Title level={3} weight="medium">
              <Trans>AI Visibility Setup</Trans>
            </Title>
            <Text variant="body2">
              <Trans>
                See how your brand appears in AI answers and discover where you can improve.
              </Trans>
            </Text>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                <Text variant="body2">
                  <Trans>Configure your brand and product areas</Trans>
                </Text>
              </li>
              <li>
                <Text variant="body2">
                  <Trans>Add competitors</Trans>
                </Text>
              </li>
              <li>
                <Text variant="body2">
                  <Trans>Start your first analysis</Trans>
                </Text>
              </li>
            </ul>
            <LinkButton
              to={ROUTES.aiVisibilitySettings.path}
              variant="on-primary"
              size="large"
              onClick={handleDismiss}
            >
              <Trans>Set up AI Visibility</Trans>
              <ArrowIcon aria-hidden="true" />
            </LinkButton>
          </article>
          <article className="flex flex-wrap items-center gap-4 rounded-2xl border border-gray-300 p-5">
            <Icon icon={ChartIcon} size="large" aria-hidden="true" />
            <div className="min-w-40 flex-1">
              <Title level={3} weight="medium">
                <Trans>Analytics Setup</Trans>
              </Title>
              <Text variant="body2" color="dark-grey">
                <Trans>Set up your tag, target market, CRM, and ad platforms.</Trans>
              </Text>
            </div>
            <Button variant="secondary" onClick={handleDismiss}>
              <Trans>Set up Analytics</Trans>
              <ArrowIcon aria-hidden="true" />
            </Button>
          </article>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
          <Text variant="footnote" color="dark-grey">
            <Trans>All setup steps live in your Health Center.</Trans>
          </Text>
          <Button variant="ghost" onClick={handleDismiss}>
            <Trans>I'll explore first</Trans>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SetupWelcome;
